import type {
  ExecutionContext,
  PhotoRepository,
  ProjectPhoto,
  RecordPhotoCommand,
} from "@dker/core";
import { DomainError } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const COLUMNS =
  "id, project_id, storage_path, taken_at, expires_at, downloaded_at, shared_with_client";

function toPhoto(row: Row): ProjectPhoto {
  return {
    id: row.id,
    projectId: row.project_id,
    storagePath: row.storage_path,
    takenAt: row.taken_at,
    expiresAt: row.expires_at,
    downloadedAt: row.downloaded_at ?? null,
    sharedWithClient: Boolean(row.shared_with_client),
  };
}

function fail(error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

export function createSupabasePhotoRepository(
  clientFor: DbClientFactory,
): PhotoRepository {
  return {
    async listByProject(
      ctx: ExecutionContext,
      projectId: string,
    ): Promise<ProjectPhoto[]> {
      const { data, error } = await clientFor(ctx)
        .from("project_photos")
        .select(COLUMNS)
        .eq("project_id", projectId)
        .is("deleted_at", null)
        .order("taken_at", { ascending: false });

      if (error) fail(error);
      return (data ?? []).map(toPhoto);
    },

    async record(
      ctx: ExecutionContext,
      command: RecordPhotoCommand,
    ): Promise<ProjectPhoto> {
      // Ni `tenant_id` ni `expires_at` dans l'insertion : le premier vient du
      // DEFAULT `public.tenant_id()`, le second d'un trigger. Les envoyer
      // depuis le client serait rouvrir exactement ce que la migration ferme.
      const { data, error } = await clientFor(ctx)
        .from("project_photos")
        .insert({
          project_id: command.projectId,
          storage_path: command.storagePath,
          uploaded_by: ctx.actorId,
        })
        .select(COLUMNS)
        .single();

      if (error) fail(error);
      return toPhoto(data);
    },

    async softDelete(ctx: ExecutionContext, photoId: string): Promise<void> {
      const { error } = await clientFor(ctx)
        .from("project_photos")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", photoId)
        .is("deleted_at", null);

      if (error) fail(error);
    },

    async markDownloaded(
      ctx: ExecutionContext,
      photoIds: readonly string[],
    ): Promise<number> {
      if (photoIds.length === 0) return 0;

      const { data, error } = await clientFor(ctx)
        .from("project_photos")
        .update({ downloaded_at: new Date().toISOString() })
        .in("id", [...photoIds])
        .select("id");

      if (error) fail(error);
      return data?.length ?? 0;
    },

    async setSharedWithClient(ctx: ExecutionContext, photoId: string, shared: boolean) {
      const { data, error } = await clientFor(ctx)
        .from("project_photos")
        .update({ shared_with_client: shared })
        .eq("id", photoId)
        .select(COLUMNS)
        .single();
      if (error) fail(error);
      return toPhoto(data as Row);
    },
  };
}
